import { PaymentAssignmentStatusId, PaymentType, Priority } from './Utils/types'

/** Ant Design Tag preset color names used across Payment Assignment UI. */
export const TAG_COLORS = {
  green: 'green',
  blue: 'blue',
  orange: 'orange',
  red: 'red',
  purple: 'purple',
  default: 'default',
} as const

export type PaymentAssignmentTagColor =
  (typeof TAG_COLORS)[keyof typeof TAG_COLORS]

export const PAYMENT_TYPE_TAG_COLORS: Record<
  PaymentType,
  PaymentAssignmentTagColor
> = {
  [PaymentType.MANUAL]: TAG_COLORS.blue,
  [PaymentType.AUTO]: TAG_COLORS.green,
  [PaymentType.BOT]: TAG_COLORS.purple,
}

export const PRIORITY_TAG_COLORS: Partial<
  Record<Priority, PaymentAssignmentTagColor>
> = {
  [Priority.EE_INQUIRY]: TAG_COLORS.red,
  [Priority.HR_INQUIRY]: TAG_COLORS.orange,
  [Priority.EXECUTIVE_INQUIRY]: TAG_COLORS.blue,
}

const PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_GREEN: PaymentAssignmentStatusId[] = [
  PaymentAssignmentStatusId.PAYMENT_CLEARED,
  PaymentAssignmentStatusId.ESCALATION_FULLY_RESOLVED,
  PaymentAssignmentStatusId.PAYMENT_SUBMITTED,
  PaymentAssignmentStatusId.EE_PAID,
  PaymentAssignmentStatusId.TERMINATED_EE,
  PaymentAssignmentStatusId.TERMINATED_PLAN,
  PaymentAssignmentStatusId.BOT_CLEARED,
  PaymentAssignmentStatusId.BOT_SUBMITTED,
]

const PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_BLUE: PaymentAssignmentStatusId[] = [
  PaymentAssignmentStatusId.AP_CONFIRMED,
  PaymentAssignmentStatusId.PENDING_AUTOPAY,
]

const PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_ORANGE: PaymentAssignmentStatusId[] =
  [
    PaymentAssignmentStatusId.REATTEMPT,
    PaymentAssignmentStatusId.PAID_ESCALATION,
  ]

const PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_RED: PaymentAssignmentStatusId[] = [
  PaymentAssignmentStatusId.BOT_FAILED,
  PaymentAssignmentStatusId.BOT_CANCELLED,
  PaymentAssignmentStatusId.UNPAID_ESCALATION,
]

const PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_PURPLE: PaymentAssignmentStatusId[] =
  [
    PaymentAssignmentStatusId.NEW_EFFECTIVE_DATE,
    PaymentAssignmentStatusId.CARRIER_SHOW_PAYMENT,
    PaymentAssignmentStatusId.BOT_PROCESSING,
  ]

const STATUS_ID_SET_GREEN = new Set(PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_GREEN)
const STATUS_ID_SET_BLUE = new Set(PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_BLUE)
const STATUS_ID_SET_ORANGE = new Set(PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_ORANGE)
const STATUS_ID_SET_RED = new Set(PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_RED)
const STATUS_ID_SET_PURPLE = new Set(PAYMENT_ASSIGNMENT_STATUS_TAG_COLOR_PURPLE)

export function getPaymentStatusColor(
  status?: PaymentAssignmentStatusId | string
): PaymentAssignmentTagColor {
  const statusIdUpper = (status ?? '').toUpperCase()
  if (!statusIdUpper) return TAG_COLORS.default

  const statusId = statusIdUpper as PaymentAssignmentStatusId
  if (STATUS_ID_SET_GREEN.has(statusId)) return TAG_COLORS.green
  if (STATUS_ID_SET_BLUE.has(statusId)) return TAG_COLORS.blue
  if (STATUS_ID_SET_ORANGE.has(statusId)) return TAG_COLORS.orange
  if (STATUS_ID_SET_RED.has(statusId) || statusIdUpper.includes('ESCALATION')) {
    return TAG_COLORS.red
  }
  if (STATUS_ID_SET_PURPLE.has(statusId)) return TAG_COLORS.purple
  return TAG_COLORS.default
}

export function getPaymentTypeTagColor(
  type?: string | PaymentType | null
): PaymentAssignmentTagColor {
  if (!type) return TAG_COLORS.default
  const normalized = String(type).toUpperCase() as PaymentType
  return PAYMENT_TYPE_TAG_COLORS[normalized] ?? TAG_COLORS.default
}

export function getPriorityTagColor(
  priority?: Priority | null
): PaymentAssignmentTagColor | '' {
  if (priority == null || priority === Priority.DEFAULT) return ''
  return PRIORITY_TAG_COLORS[priority] ?? TAG_COLORS.default
}

export const STATUS_PILL_COLOR_MAP: Record<
  PaymentAssignmentTagColor,
  { backgroundColor: string; textColor: string }
> = {
  [TAG_COLORS.green]: { backgroundColor: '#f6ffed', textColor: '#389e0d' },
  [TAG_COLORS.blue]: { backgroundColor: '#e6f4ff', textColor: '#0958d9' },
  [TAG_COLORS.orange]: { backgroundColor: '#fff7e6', textColor: '#d46b08' },
  [TAG_COLORS.red]: { backgroundColor: '#fff1f0', textColor: '#cf1322' },
  [TAG_COLORS.purple]: { backgroundColor: '#f9f0ff', textColor: '#531dab' },
  [TAG_COLORS.default]: { backgroundColor: '#f2f4f6', textColor: '#3a4951' },
}

export function getStatusPillColors(statusId?: string) {
  if (!statusId) return STATUS_PILL_COLOR_MAP[TAG_COLORS.default]
  const antColor = getPaymentStatusColor(statusId)
  return (
    STATUS_PILL_COLOR_MAP[antColor] ?? STATUS_PILL_COLOR_MAP[TAG_COLORS.default]
  )
}
